import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, ScrollRestoration } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastProvider } from "@/contexts/ToastContext";
import AOS from "aos";
import "aos/dist/aos.css";

// Components
import Header from "@/components/common/Header";
import Footer from "@/components/common/Footer";
import LoadingScreen from "@/components/common/LoadingScreen";
import CustomCursor from "@/components/common/CustomCursor";
import ScrollProgress from "@/components/common/ScrollProgress";

// Pages/Sections - Home
import HeroSection from "@/components/hero/HeroSection";
import AboutSection from "@/components/about/AboutSection";
import ProjectsSection from "@/components/projects/ProjectsSection";
import ExperienceSection from "@/components/experience/ExperienceSection";
import ContactSection from "@/components/contact/ContactSection";
import BooksSection from "@/components/books/BooksSection";
import TestimonialsSection from "@/components/testimonials/TestimonialsSection";
import BlogPreview from "@/components/blog/BlogPreview";

// New Pages
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import BookDetail from "@/pages/BookDetail";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PaymentVerify from "@/pages/PaymentVerify";
import Dashboard from "@/pages/Dashboard";

// Admin Pages
import ProtectedRoute from "@/components/admin/ProtectedRoute";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import BlogManager from "@/pages/admin/BlogManager";
import ProductManager from "@/pages/admin/ProductManager";
import OrderManager from "@/pages/admin/OrderManager";
import UserManager from "@/pages/admin/UserManager";
import Settings from "@/pages/admin/Settings";

// Styles
import "@/styles/globals.css";
import "@/styles/animations.css";

// Hooks
import { useTheme } from "@/hooks/useTheme";

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Home Page Component
const HomePage = () => {
  return (
    <>
      <HeroSection />
      <AboutSection />
      <ProjectsSection />
      <ExperienceSection />
      <TestimonialsSection />
      <BooksSection />
      <BlogPreview />
      <ContactSection />
    </>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Layout wrapper component to conditionally render Header/Footer
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <>
      <ScrollToTop />
      {!isAdminRoute && <Header />}
      {children}
      {!isAdminRoute && <Footer />}
    </>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const { theme } = useTheme();

  useEffect(() => {
    // Initialize AOS animations
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
    });

    // Simulate a loading screen
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Sync theme class with <html>
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <Router>
            <div
              className={`min-h-screen bg-white dark:bg-dark-900 text-gray-900 dark:text-white transition-colors duration-300`}
            >
              {/* Show loading screen */}
              {isLoading && <LoadingScreen />}

              {/* Global UI elements */}
              <CustomCursor />
              <ScrollProgress />

              <AppLayout>

            <main className="relative">
              <Routes>
                {/* Home route with all sections */}
                <Route path="/" element={<HomePage />} />

                {/* Blog routes */}
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />

                {/* Books routes */}
                <Route path="/books/:id" element={<BookDetail />} />

                {/* Shop routes */}
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/:slug" element={<ProductDetail />} />

                {/* Auth routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* User Dashboard - Protected */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />

                {/* Payment verification */}
                <Route path="/payment/verify" element={<PaymentVerify />} />

                {/* Admin routes - Protected */}
                <Route path="/admin" element={
                  <ProtectedRoute adminOnly={true}>
                    <AdminLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<AdminDashboard />} />
                  <Route path="blog" element={<BlogManager />} />
                  <Route path="products" element={<ProductManager />} />
                  <Route path="orders" element={<OrderManager />} />
                  <Route path="users" element={<UserManager />} />
                  <Route path="settings" element={<Settings />} />
                </Route>

                {/* Individual section routes (keeping compatibility) */}
                <Route path="/about" element={<AboutSection />} />
                <Route path="/projects" element={<ProjectsSection />} />
                <Route path="/experience" element={<ExperienceSection />} />
                <Route path="/contact" element={<ContactSection />} />

                {/* 404 - Add a not found route */}
                <Route path="*" element={
                  <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
                    <div className="text-center">
                      <h1 className="text-6xl font-bold mb-4">404</h1>
                      <p className="text-xl text-gray-400 mb-8">Page not found</p>
                      <a href="/" className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors">
                        Go Home
                      </a>
                    </div>
                  </div>
                } />
              </Routes>
            </main>
            </AppLayout>
          </div>
        </Router>
        </ToastProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
